"use client";

import { useParams, usePathname, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { v4 as uuid } from "uuid";

import {
  CYPRESS_REDIRECT_RETURN_FLAG,
  EVENT_ATTRIBUTION_PARAMS_UPDATED,
  EVENT_CLICK_ID_UPDATED,
  EVENT_JOURNEY_UPDATED,
  EVENT_LANDING_PAGE_VIEWED,
  EVENT_VISIT_COOKIE_READY,
  EXTERNAL_REDIRECT_SITES,
  EXTERNAL_RETURN_VISIT_FLAG,
  JOURNEY_VARIATION_PARAM_NAME,
  PPC_JOURNEY_NAME,
} from "@shared/constants";

import { logDebug } from "@shared/functions/log";
import { safeParseQueryParams } from "@shared/functions/safeParseQueryParams";

import DataLayer from "@client/classes/data-layer/data-layer";
import {
  hasAttributionChanged,
  hasAttributionParams,
  hasClickIdOnlyChanged,
} from "@client/components/analytics/functions";
import createVisitCookie from "@client/cookies/createVisitCookie";
import getReturningVisitorCookie from "@client/cookies/getReturningVisitorCookie";
import getVisitCookie from "@client/cookies/getVisitCookie";

import type Visit from "@packages/types/visit";

interface Props {
  visitId?: string;
  visitIdGeneratedByMiddleware: boolean;
}

export default function VisitCookie({
  visitId: _,
  visitIdGeneratedByMiddleware,
}: Props) {
  const params = useParams<{
    journey?: string;
  }>();

  const searchParams = useSearchParams();

  const pathname = usePathname();

  useEffect(() => {
    // Early return during SSR when window is not defined
    if (typeof window === "undefined") return;

    // Early return if we're on the redirect page
    // to prevent visit cookie from being created on redirect page
    const isClientSideRedirectPage =
      pathname.startsWith("/redirect") ||
      (searchParams.get("redirectUrl") && searchParams.get("originalUrl"));

    if (isClientSideRedirectPage) {
      return;
    }
    const visitCookie = getVisitCookie();
    const referredBy = searchParams.get(EXTERNAL_RETURN_VISIT_FLAG);
    const hasCypressFlag = searchParams.get(CYPRESS_REDIRECT_RETURN_FLAG);

    const externalSites = hasCypressFlag
      ? ["google.com", ...EXTERNAL_REDIRECT_SITES]
      : EXTERNAL_REDIRECT_SITES;

    let isAllowedExternalReferer = false;
    let externalReferer = "";
    if (
      referredBy &&
      externalSites.some((site) => {
        const isKnownExternalRedirect = referredBy?.includes(site);

        if (isKnownExternalRedirect) {
          externalReferer = site;
        }

        return isKnownExternalRedirect;
      })
    ) {
      isAllowedExternalReferer = true;
    }

    // Check for existence of returningVisitor and returningVisitorOriginalIngressUrl cookies
    let DEFER_TO_ORIGINAL_INGRESS_URL = false;

    const returnUserDetails = getReturningVisitorCookie();
    const { returnVisitOriginalIngressCookie, returnVisitCookie } =
      returnUserDetails || {};

    if (returnVisitCookie && returnVisitOriginalIngressCookie) {
      DEFER_TO_ORIGINAL_INGRESS_URL = isAllowedExternalReferer;
    }

    const { queryString: mostRecentQueryString, safeReconstructedUrl } =
      safeParseQueryParams(
        DEFER_TO_ORIGINAL_INGRESS_URL
          ? returnVisitOriginalIngressCookie
          : window.location.href,
        externalReferer,
      );

    // NEW INGRESS ----------------------------------------------
    const newIngress = !visitCookie?.unalteredIngressUrl;

    //JOURNEY ----------------------------------------------

    const journeyChanged =
      params?.journey && visitCookie?.journey
        ? params.journey !== visitCookie?.journey &&
          params.journey !== PPC_JOURNEY_NAME
        : false;

    const journey = journeyChanged
      ? params?.journey
      : visitCookie?.journey || params?.journey || "";

    //QUERY STRING ----------------------------------------------
    let attributionRelatedSearchParamsChanged = false;
    let clickIdOnlyChanged = false;
    const visitCookieQueryString = visitCookie?.queryString || "";

    // Check if attribution params have changed between current URL and ingress URL
    // Compare against unalteredIngressUrl to determine if attribution has actually changed
    const ingressUrl = visitCookie?.unalteredIngressUrl || "";
    const currentUrl = safeReconstructedUrl;

    // Check if they have an ingress URL from a previous page load.
    // For allow-listed external referrers we intentionally *do not* treat the redirect
    // as a new visit or re-evaluate attribution (beyond allowing a click ID update),
    // because we want to preserve the original ingress/source for flows such as the
    // advertorial redirect from stripes.com. If the allow-list (`EXTERNAL_REDIRECT_SITES`)
    // or our attribution rules change, revisit this branch to confirm that skipping
    // full attribution comparison is still the correct behavior.
    if (ingressUrl && isAllowedExternalReferer) {
      // no need to check if search params changed, they will on an external referer
      clickIdOnlyChanged = hasClickIdOnlyChanged(
        currentUrl,
        ingressUrl,
        attributionRelatedSearchParamsChanged,
      );
    } else if (mostRecentQueryString && ingressUrl) {
      // Use the attribution comparison logic
      attributionRelatedSearchParamsChanged = hasAttributionChanged(
        currentUrl,
        ingressUrl,
      );
      // Check if only the click ID changed (attribution stayed the same)
      clickIdOnlyChanged = hasClickIdOnlyChanged(
        currentUrl,
        ingressUrl,
        attributionRelatedSearchParamsChanged,
      );
      // on first ingress there will be no ingressUrl at this stage
    } else if (mostRecentQueryString && !ingressUrl) {
      // No ingress URL exists, check if current has attribution
      attributionRelatedSearchParamsChanged = hasAttributionParams(currentUrl);
    }

    const queryString = attributionRelatedSearchParamsChanged
      ? mostRecentQueryString
      : visitCookieQueryString || mostRecentQueryString;

    //NEW VISIT ----------------------------------------------
    const newVisit =
      !visitCookie ||
      journeyChanged ||
      attributionRelatedSearchParamsChanged ||
      newIngress;

    //LANDING PAGE ----------------------------------------------
    const landingPage = newVisit
      ? pathname // Use pathname from Next.js hook instead of window.location
      : visitCookie?.landingPage;

    //INGRESS URL ----------------------------------------------
    const unalteredIngressUrl = newVisit
      ? safeReconstructedUrl
      : visitCookie?.unalteredIngressUrl;

    //VISIT ID ----------------------------------------------

    // The server handles this on the first request, so we do not need to
    // predicate this on newIngress
    let generateNewVisitId =
      !visitCookie || journeyChanged || attributionRelatedSearchParamsChanged;

    if (visitIdGeneratedByMiddleware) {
      generateNewVisitId = false;
    }

    const vId = generateNewVisitId ? uuid() : visitCookie?.visitId;

    // GEO LOCATION ----------------------------------------------
    const geoLocation = visitCookie?.geoLocation || ({} as Visit.Geolocation);

    logDebug({
      message: "VisitCookieLogic for debug",
      event: {
        name: "VisitCookieLogic",
        meta: {
          newVisit,
          visitCookie,
          journey,
          journeyChanged,
          paramsJourney: params?.journey,
          searchParams,
          pathname,
          visitCookieJourney: visitCookie?.journey,
          attributionRelatedSearchParamsChanged,
          clickIdOnlyChanged,
          mostRecentQueryString,
          visitCookieQueryString,
          queryString,
          newIngress,
          landingPage,
          safeReconstructedUrl,
          unalteredIngressUrl,
          generateNewVisitId,
          vId,
          visitIdGeneratedByMiddleware,
          geoLocation,
          returnVisitOriginalIngressCookie,
          returnVisitCookie,
          isAllowedExternalReferer,
          DEFER_TO_ORIGINAL_INGRESS_URL,
        },
      },
    });

    // COOKIE CREATE / UPDATE ----------------------------------------------

    // If journey == ppc then set journey to variation param
    // This lets us use dynamic journeys for ppc without exposing them in the URL
    let updatedJourney = journey;

    if (
      params.journey === PPC_JOURNEY_NAME &&
      searchParams.get(JOURNEY_VARIATION_PARAM_NAME)
    ) {
      updatedJourney =
        searchParams.get(JOURNEY_VARIATION_PARAM_NAME) || journey;
    }

    const updatedVisitCookie: Visit.Cookie = {
      ...visitCookie,
      geoLocation,
      journey: updatedJourney,
      landingPage,
      visitIdGeneratedByMiddleware: false,
      queryString: DEFER_TO_ORIGINAL_INGRESS_URL
        ? mostRecentQueryString
        : queryString,
      unalteredIngressUrl: DEFER_TO_ORIGINAL_INGRESS_URL
        ? safeReconstructedUrl
        : unalteredIngressUrl,
      visitId: `${vId}`,
    };

    createVisitCookie(updatedVisitCookie);

    // If returningVisitor, we need more than just newIngress
    // to determine if we should fire the landing page event
    // newVisit should determine if we should fire the landing page event
    // which itself fires postVisit (which associates user id with visit id)
    const shouldFireLandingPageEvent =
      newVisit || DEFER_TO_ORIGINAL_INGRESS_URL;

    // FIRE EVENTS ----------------------------------------------
    if (shouldFireLandingPageEvent)
      DataLayer.events.queue(EVENT_LANDING_PAGE_VIEWED);

    if (attributionRelatedSearchParamsChanged)
      DataLayer.events.queue(EVENT_ATTRIBUTION_PARAMS_UPDATED);

    if (clickIdOnlyChanged) DataLayer.events.queue(EVENT_CLICK_ID_UPDATED);

    if (journeyChanged) DataLayer.events.queue(EVENT_JOURNEY_UPDATED);

    DataLayer.events.queue(EVENT_VISIT_COOKIE_READY);
  }, [params?.journey, searchParams, pathname, visitIdGeneratedByMiddleware]);

  return null;
}
